import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BleDevice } from '@capacitor-community/bluetooth-le';
import { FormHelper } from 'apps/idl/src/helpers';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { CapacitorPluginScePlugin, StatusSCEPlugin } from 'capacitor-plugin-sce';
import { defer, EMPTY, from, iif, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, concatMap, delay, filter, switchMap, take, takeUntil, tap, timeout } from 'rxjs/operators';
import { PlatformService } from '../../../../../../services/security';
import { SceEventService } from '../../../../sce4Mobile/sce-event.service';
import { SceStatus } from '../../../../sce4Mobile/sce-status';
import { SceFactory } from '../../../../sce4Mobile/sceFactory';
import { EDeviceType } from '../../../enums/EDeviceType';
import { BluetoothService } from '../../../services/bluetooth.service';
import { DrawerPopoverService } from '../../../services/drawer-popover.service';
import { LecteurStorageService } from '../../../services/lecteurStorage.service';
import { PanneauService } from '../../../services/panneau.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { MenuSCELogsComponent } from '../../menu-sce-logs/menu-sce-logs.component';

@Component({
	selector: 'di-panneau-tester-lecteur',
	templateUrl: './panneau-tester-lecteur.component.html',
	styleUrls: ['./panneau-tester-lecteur.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PanneauTesterLecteurComponent extends DestroyableComponentBase implements OnInit, OnDestroy{

	public demandeCodeCPS : boolean = false;
	public cpsSaisie : string = "";
	public errorCodeShow : boolean = false;
	public errorCodeMessage : string = "";
	public sceLogs: string[] = [];
	public showPopover :boolean = false;
	public lecteurName: string;
	private sceStatusSubscription?: Subscription;
	
	constructor(
		private svcPanneau: PanneauService,
		private svcSceEvent : SceEventService,
		private svcSnackbar: SnackbarService,        
		public cdr: ChangeDetectorRef,
		private svcDrawerPopover :  DrawerPopoverService,
		private svcBluetooth: BluetoothService,
		private svcLecteurStorage : LecteurStorageService,
		private svcPlatform : PlatformService
	) {
		super();
	 }

	public pluginSce : CapacitorPluginScePlugin;
	 

	public async ngOnInit() {

		const deviceTypeUser: EDeviceType = this.svcPlatform.getDeviceType();
		this.pluginSce = SceFactory.getSceInstance(deviceTypeUser)

		this.svcSceEvent.sce_log$.pipe(
			takeUntil(this.destroyed$),
			tap(msg => {
				if (msg) {
					this.sceLogs.unshift(msg);
					this.cdr.detectChanges();
				}
			})
		).subscribe();

		this.listenToSceStatus();
		const sceStatus = await this.pluginSce.getStatus()
		iif(() => !sceStatus.connected && !sceStatus.connecting,			
			defer(() => {
				console.warn('[COMP] Connexion au SCE en cours...');
				return from(this.pluginSce.connect());
			}),
			defer(() => {
				this.retrySce();
				console.warn('[COMP] Déconnexion du SCE en cours...');
				return from(this.pluginSce.disconnect());
			})
		).pipe(
			takeUntil(this.destroyed$),
			catchError((err) => {
				console.error('[COMP] Erreur lors du connectToSCE initial', err);
				this.svcSceEvent.emitStatus('error');
				return EMPTY;
			})
		).subscribe();
	}

	public retrySce(): void {
		this.svcSceEvent.sceDisconnect$.pipe(
			take(1),
			takeUntil(this.destroyed$),
			tap(() => {
				console.warn('[COMP] Connexion au SCE en cours...');
			}),
			concatMap(() => from(this.pluginSce.connect())),
			timeout(15000), 
			catchError(error => {
					this.svcSnackbar.showToast("error", "bottom center", "Erreur lors de la connexion au lecteur, veuillez réessayer.", null, null, null, "6000");
					this.svcDrawerPopover.close();
					this.svcPanneau.close();
					console.error('Timeout: aucune réponse après 15 secondes');
					return of(null); 
				}
			)
		).subscribe();
	}

	public override ngOnDestroy() {
		if (this.sceStatusSubscription) {
			this.sceStatusSubscription.unsubscribe();
		}
		from(this.pluginSce.getStatus()).pipe(
			switchMap((status: StatusSCEPlugin) => {
			if (status.connected) {
			return from(this.pluginSce.disconnect());
			}
			return EMPTY;
			})
		).subscribe();
		super.ngOnDestroy();
	}

	public openMenu(event: Event): void {
		this.showPopover = true;
		const menuSCELogsInputs = {
			
		}
		this.svcDrawerPopover.open("Gestions des logs", "auto", event.currentTarget, MenuSCELogsComponent, menuSCELogsInputs, () => this.showPopover = false)
	}

	private listenToSceStatus() {
		if (this.sceStatusSubscription) {
			this.sceStatusSubscription.unsubscribe();
			this.sceStatusSubscription = undefined;
		}
		
		this.sceStatusSubscription = this.svcSceEvent.sceStatus$.pipe(
			takeUntil(this.destroyed$),
			filter(sceStatus => !!sceStatus),
			switchMap((sceStatus: SceStatus) => this.handleSceStatus(sceStatus)),
			catchError(err => {
				console.error('[COMP] SCE status handling error:', err.message);
				this.svcSnackbar.showToast("error", "bottom center", `La connexion au lecteur SCE a échoué. Essayez de redémarrer le lecteur ou de relancer l'application.`, null, null, null, "6000");
				this.svcPanneau.close();
				return EMPTY;
			})
		).subscribe();
	}

	public handleSceStatus(sceStatus: SceStatus): Observable<SceStatus | void> {
		switch (sceStatus.status) {
			case 'waiting-deviceId':
				this.svcSnackbar.showToast("error", "bottom center", "SCE non utilisable pour le moment. Veuillez enregistrer l'appareil dans Paramétrages > Lecteurs.", null, null, null, "6000");
				return throwError(new Error(`Erreur SCE: ${sceStatus.message ?? "Erreur Inconnue"}`));
			case 'connected':
				console.warn('[COMP] Le SCE est prêt.');
				return from(this.pluginSce.getStatus()).pipe(
					delay(2000),
					switchMap((status: StatusSCEPlugin) => {
						if (status.reader_connected) {
							return of(sceStatus);
						}
						return from(this.svcLecteurStorage.getLecteurName()).pipe(
							concatMap((lecteurName: string) => {
								if (!lecteurName) {
									this.svcSnackbar.showToast("error", "bottom center", `Aucun lecteur Bluetooth connecté. Veuillez le connecter dans Paramétrages > Lecteurs`, null, null, null, "6000");
									return EMPTY;
								}
								this.lecteurName = lecteurName;
								return from(this.svcBluetooth.scanForLecteur(this.lecteurName));
							}),
							concatMap((device: BleDevice | null) => {
								if (!device) {
									this.svcSnackbar.showToast("warning", "bottom center", `Le lecteur Bluetooth n'a pas été détecté.`, null, null, null, "6000");
									return EMPTY;
								}
								return from(this.pluginSce.connectToDevice({deviceName: this.lecteurName}));
							})
						);
					})
				);
			case 'waiting-lecteur':
				return of(sceStatus);
			case 'error':
				console.warn('[COMP] Une erreur a été détectée dans le processus SCE.');
				this.cdr.detectChanges();
				return of(sceStatus);
			case 'waiting-code':
				console.warn('[COMP] Demande du code porteur.');
				this.demandeCodeCPS = true;
				this.cdr.detectChanges();
				return of(sceStatus);
			case 'error-code':
				console.warn('[COMP] Une erreur a été détectée dans le processus SCE : ' + sceStatus.message);
				this.errorCodeShow = true;
				this.demandeCodeCPS = true;
				this.cpsSaisie = "";
				this.errorCodeMessage = sceStatus.message;
				this.cdr.detectChanges();
				return of(sceStatus)
			case 'ready-code':	
				console.warn('[COMP] Code porteur déjà saisi, lancement de la lecture CV ...');
				return of(sceStatus);
			case 'ready':
				console.warn('[COMP] Le lecteur est prêt.');
				return of(sceStatus);
			case 'warning':
				console.warn('[COMP] Une erreur a été détectée dans le processus SCE : ' + sceStatus.message);
				return of(sceStatus);
		}
		this.cdr.detectChanges();
		return of(sceStatus);
	}
	

	public sendCodeCPS(){
		this.pluginSce.setCarrierCode({carrierCode : this.cpsSaisie});
		this.demandeCodeCPS = false;
		this.errorCodeShow = false;
	}

	public validateCpsSaisie(){
		this.cpsSaisie = FormHelper.validCPSCode(this.cpsSaisie);
		this.cdr.detectChanges()
	}
}
